import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../lib/api";
import Button from "../ui/Button";
import { useUpdateDemo } from "../../hooks/useDemoApi";
import { useAuth } from "../../hooks/AuthContext";


export const EditDemoModal = ({ demo, onClose }) => {
  const [employees, setEmployees] = useState([]);
  const [dateTime, setDateTime] = useState("");
  const [type, setType] = useState("training"); // default fallback

  const { register, handleSubmit, reset, setValue, getValues } = useForm();
  const { mutate: updateDemo, isLoading } = useUpdateDemo();
  const { user } = useAuth();

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await api.get("/employees");
        setEmployees(res.data.data.employees);
      } catch (err) {
        console.error("Error fetching employees:", err);
      }
    };
    fetchEmployees();
  }, []);

  // Reset form when demo changes
  useEffect(() => {
    if (demo) {
      const timestamp = Number(demo.datetime) * 1000;
      const date = new Date(timestamp);

      const pad = (n) => n.toString().padStart(2, "0");
      const localDateTime = `${date.getFullYear()}-${pad(
        date.getMonth() + 1
      )}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
      setDateTime(localDateTime);

      // Determine type from demo data and set as default
      const prefix = demo.type || demo.demoName?.split(" - ")[0] || "training";
      setType(prefix);

      // Capitalize prefix ONLY for demoName display
      const capitalizedPrefix = prefix.charAt(0).toUpperCase() + prefix.slice(1).toLowerCase();

      // Format demoName: "Demo/Training - 28 Oct 2025 - 04:08 pm"
      const day = date.getDate().toString().padStart(2, "0");
      const month = date.toLocaleString("en-US", { month: "short" });
      const year = date.getFullYear();
      let hours = date.getHours();
      const minutes = pad(date.getMinutes());
      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12 || 12;
      const hourStr = hours.toString().padStart(2, "0");
      const demoNameFormatted = `${capitalizedPrefix} - ${day} ${month} ${year} - ${hourStr}:${minutes} ${ampm}`;

      reset({
        demoName: demoNameFormatted,
        hostBy: demo.hostBy?._id || "",
        createdBy: demo.createdBy?._id || "",
        meetingLink: demo.meetingLink || "",
        status: demo.status || "pending",
        datetime: localDateTime,
        type: prefix,
        updatedBy: user?.employeeProfile?._id || "",
        _id: demo._id || "",
      });
    }
  }, [demo, reset, user?.employeeProfile?._id]);

  const onSubmit = (formData) => {
    // Capitalize the first letter of demoName before sending
    const capitalizedDemoName = formData.demoName
      ? formData.demoName.charAt(0).toUpperCase() + formData.demoName.slice(1)
      : formData.demoName;

    const updatedData = {
      ...formData,
      demoName: capitalizedDemoName,
      datetime: formData.datetime
        ? Math.floor(new Date(formData.datetime).getTime() / 1000)
        : undefined,
      type,
    };

    updateDemo(
      { id: demo._id, data: updatedData, updatedBy: user?.employeeProfile?._id },
      {
        onSuccess: () => {
          toast.success("Function updated successfully!");
          onClose();
        },
        onError: (error) => {
          toast.error(error.response?.data?.message || error.message);
        },
      }
    );
  };

  if (!demo) return null;

  // Helper function to update demoName based on type and datetime
  const updateDemoName = (newDateTime, newType = type) => {
    const dateObj = new Date(newDateTime);
    const day = dateObj.getDate().toString().padStart(2, "0");
    const month = dateObj.toLocaleString("en-US", { month: "short" });
    const year = dateObj.getFullYear();
    let hours = dateObj.getHours();
    const minutes = dateObj.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12 || 12;
    const hourStr = hours.toString().padStart(2, "0");

    // Capitalize the type for demoName display
    const capitalizedType = newType.charAt(0).toUpperCase() + newType.slice(1).toLowerCase();
    const newDemoName = `${capitalizedType} - ${day} ${month} ${year} - ${hourStr}:${minutes} ${ampm}`;
    setValue("demoName", newDemoName);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex justify-center items-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg  p-3 px-5 transition-all scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
        {/* Header */}
        <div className="flex justify-between items-center mb-5 border-b pb-1 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-800">Edit Function</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="">
          {/* Demo Name */}
          <div className="mb-3">
            <label className="text-gray-700 mb-1 block text-sm font-medium">Function Name</label>
            <p className="p-2 border text-xs uppercase rounded-lg bg-gray-100">{getValues("demoName")}</p>
          </div>

          {/* Host By */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col">
              <label className="text-gray-700 mb-1 block text-sm font-medium">Host By</label>
              <select
                {...register("hostBy")}
                className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                defaultValue={demo?.hostBy?._id || ""}
              >
                {employees.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Created By */}
            <div className="flex flex-col">
              <label className="text-gray-700 mb-1 block text-sm font-medium">Created By</label>
              <select
                {...register("createdBy")}
                className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                defaultValue={demo?.createdBy?._id || ""}
              >
                {employees.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Status */}
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="flex flex-col">
              <label className="text-gray-700 mb-1 block text-sm font-medium">Status</label>
              <select
                {...register("status")}
                className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            {/* Date & Time */}
            <div className="flex flex-col">
              <label className="text-gray-700 mb-1 block text-sm font-medium">Date & Time</label>
              <input
                type="datetime-local"
                value={dateTime}
                onChange={(e) => {
                  const val = e.target.value;
                  setDateTime(val);
                  setValue("datetime", val, { shouldDirty: true, shouldValidate: true });
                  updateDemoName(val); // update demoName dynamically
                }}
                className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Type Selector */}
          <div className="flex flex-col mt-3">
            <label className="text-gray-700 mb-1 block text-sm font-medium">Type</label>
            <select
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                updateDemoName(dateTime, e.target.value); // update demoName prefix
              }}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="demo">Demo</option>
              <option value="training">Training</option>
            </select>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-2 sticky bottom-0 bg-white pb-2 mt-3">
            <Button type="button" variant="secondary" onClick={onClose} className="px-4 py-2">
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isLoading} className="px-4 py-2">
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
